import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography } from 'antd';
import { GoogleCircleFilled } from '@ant-design/icons';
import firebase from 'firebase/app';

import { signInWithProviderFlow } from '../../utils/authFlow';

const { Text } = Typography;

/**
 * Google Sign In button for clients (replaces the FirebaseUI Google flow).
 * Popup on desktop, redirect on mobile (authFlow); we control the UI and route the result through the same
 * handler FirebaseUI used — preserving the anti-duplicate abort + human-verify checks.
 * Calls handler(authResult, null) on success, handler(null, error) on failure.
 */
export default function ClientGoogleLogin({ handler, isSignup }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      const provider = new firebase.auth.GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      provider.setCustomParameters({ prompt: 'select_account' });
      // Mobile browsers block popups — redirect there; popup on desktop. A null
      // result means the page is navigating away (redirect flow): the result is
      // consumed by getRedirectResult() in ClientLoginScreen after the round-trip.
      // isSignup rides along in sessionStorage so the anti-duplicate abort still
      // knows this was a deliberate signup after the redirect unload.
      const authResult = await signInWithProviderFlow(firebase.auth(), provider, { inSignupFlow: !!isSignup });
      if (authResult && mountedRef.current && handler) handler(authResult, null);
    } catch (err) {
      // User dismissed the popup — not an error worth surfacing.
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        if (mountedRef.current) setLoading(false);
        return;
      }
      if (mountedRef.current) {
        setError(err.message || 'Google sign-in failed');
        if (handler) handler(null, err);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  return (
    <div>
      <Button
        block
        size="large"
        onClick={handleGoogleSignIn}
        loading={loading}
        icon={<GoogleCircleFilled />}
        style={{
          backgroundColor: '#fff',
          color: '#333',
          borderColor: '#ddd',
          height: 44,
        }}
      >
        {isSignup ? 'Sign up with Google' : 'Sign in with Google'}
      </Button>
      {error && (
        <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
          {error}
        </Text>
      )}
    </div>
  );
}
